import axios from 'axios';

// Always use same-origin to avoid cross-domain issues with custom domains
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

// JWT interceptor - attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 + drop cached lists after any mutation
api.interceptors.response.use(
  (response) => {
    if (response.config?.method && response.config.method.toLowerCase() !== 'get') listCache.clear();
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Short-lived in-memory cache for rarely-changing reference lists (departments, branches, reps, ...).
// Same promise is shared by concurrent callers; any non-GET request clears the whole cache.
const CACHE_TTL_MS = 60000;
const listCache = new Map();
const clone = (d) => (d === undefined ? d : JSON.parse(JSON.stringify(d)));
function cachedGet(url, config) {
  const key = url + (config?.params ? '?' + new URLSearchParams(config.params).toString() : '');
  const hit = listCache.get(key);
  if (hit && hit.expires > Date.now()) return hit.promise.then((r) => ({ ...r, data: clone(r.data) }));
  const promise = api.get(url, config).catch((e) => { listCache.delete(key); throw e; });
  listCache.set(key, { promise, expires: Date.now() + CACHE_TTL_MS });
  return promise.then((r) => ({ ...r, data: clone(r.data) }));
}

// Auth
export const authLogin = (email, password) => api.post('/auth/login', { email, password });
export const authMe = () => api.get('/auth/me');
export const authLogout = async () => {
  try { await api.post('/auth/logout'); } catch { /* token may already be invalid */ }
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};
export const getUsers = () => api.get('/auth/users');
export const createUser = (data) => api.post('/auth/users', data);
export const updateUser = (id, data) => api.put(`/auth/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/auth/users/${id}`);
export const changePassword = (data) => api.put('/auth/change-password', data);

// Departments
export const getDepartments = () => cachedGet('/departments');
export const createDepartment = (data) => api.post('/departments', data);
export const updateDepartment = (id, data) => api.put(`/departments/${id}`, data);
export const deleteDepartment = (id) => api.delete(`/departments/${id}`);
export const getDeptApiSettings = (id) => api.get(`/departments/${id}/api-settings`);
export const updateDeptApiSettings = (id, data) => api.put(`/departments/${id}/api-settings`, data);

// Appointments
export const getAppointments = (params) => api.get('/appointments', { params }).then(res => ({ ...res, data: res.data.items || res.data }));
export const createAppointment = (data) => api.post('/appointments', data);
export const updateAppointment = (id, data) => api.put(`/appointments/${id}`, data);
export const deleteAppointment = (id) => api.delete(`/appointments/${id}`);
export const getAvailableSlots = (deptId, date, branchId) => api.get('/appointments/available-slots', { params: { department_id: deptId, date, branch_id: branchId } });
export const getAvailability = (params) => api.get('/availability', { params });
export const createAvailability = (data) => api.post('/availability', data);
export const deleteAvailability = (id) => api.delete(`/availability/${id}`);

// KVKK
export const sendKvkkCode = (data) => api.post('/kvkk/send-code', data);
export const verifyKvkkCode = (phone, code) => api.post(`/kvkk/verify-code?phone=${phone}&code=${code}`);
export const getKvkkConsents = () => api.get('/kvkk/consents');
export const checkKvkkConsent = (phone) => api.get(`/kvkk/check/${phone}`);

// Brands
export const getBrands = () => cachedGet('/brands');
export const createBrand = (data) => api.post('/brands', data);
export const updateBrand = (id, data) => api.put(`/brands/${id}`, data);
export const deleteBrand = (id) => api.delete(`/brands/${id}`);

// Models
export const getModels = (brandId) => api.get('/models', { params: brandId ? { brand_id: brandId } : {} });
export const createModel = (data) => api.post('/models', data);
export const updateModel = (id, data) => api.put(`/models/${id}`, data);
export const deleteModel = (id) => api.delete(`/models/${id}`);
export const notifyPriceChange = (id) => api.post(`/models/${id}/notify-price-change`);
export const downloadModelTemplate = () => `${API}/models/template`;
export const uploadModelsExcel = (brandId, file, replaceAll = false) => {
  return api.post(`/models/upload-excel?brand_id=${brandId}&replace_all=${replaceAll}`, file, {
    headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
  });
};
export const uploadModelImage = (modelId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/model-image/${modelId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getModelImageUrl = (modelId) => `${API}/files/model-image/${modelId}`;

// Branches
export const getBranches = () => cachedGet('/branches');
export const createBranch = (data) => api.post('/branches', data);
export const updateBranch = (id, data) => api.put(`/branches/${id}`, data);
export const deleteBranch = (id) => api.delete(`/branches/${id}`);

// Campaigns
export const getCampaigns = () => cachedGet('/campaigns');
export const createCampaign = (data) => api.post('/campaigns', data);
export const updateCampaign = (id, data) => api.put(`/campaigns/${id}`, data);
export const deleteCampaign = (id) => api.delete(`/campaigns/${id}`);

// Leads
export const getLeads = (filters) => api.get('/leads', { params: filters });
export const createLead = (data) => api.post('/leads', data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const getLeadDetail = (id) => api.get(`/leads/${id}`);
export const addLeadActivity = (id, data) => api.post(`/leads/${id}/activity`, data);
export const notifyRepForLead = (id, message = "") => api.post(`/leads/${id}/notify-rep`, { message });
export const exportLeads = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return `${API}/leads/export${q ? '?' + q : ''}`;
};

// Sales Reps
export const getSalesReps = () => cachedGet('/sales-reps');
export const getRepWorkload = () => api.get('/sales-reps/workload');
export const setMyLeave = (on_leave) => api.put('/my/leave', { on_leave });
export const createSalesRep = (data) => api.post('/sales-reps', data);
export const updateSalesRep = (id, data) => api.put(`/sales-reps/${id}`, data);
export const deleteSalesRep = (id) => api.delete(`/sales-reps/${id}`);
export const getRepDuplicates = () => api.get('/sales-reps/duplicates');
export const mergeSalesReps = (source_rep_id, target_rep_id, dry_run = false) => api.post('/sales-reps/merge', { source_rep_id, target_rep_id, dry_run });

// Conversations
export const getConversations = (params) => api.get('/conversations', { params });
export const getConversation = (id) => api.get(`/conversations/${id}`);
export const addConversationNote = (id, data) => api.post(`/conversations/${id}/note`, data);
export const getConversationNotes = (id) => api.get(`/conversations/${id}/notes`);
export const getFollowUpPending = () => api.get('/conversations/follow-up/pending');
export const assignConversation = (id, rep) => api.put(`/conversations/${id}/assign`, { assigned_rep: rep });
export const initiateConversation = (data) => api.post('/conversations/initiate', data);
export const sendAdminMessage = (id, data) => api.post(`/conversations/${id}/message`, data);
export const toggleConvoAI = (id) => api.put(`/conversations/${id}/ai-toggle`);
export const sendVoiceMessage = (id, blob, duration = 0, ext = "webm") => {
  const form = new FormData();
  form.append("file", blob, `voice.${ext}`);
  form.append("duration", String(duration));
  return api.post(`/conversations/${id}/voice`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
};
export const getWhatsappTemplates = (departmentId) => api.get('/whatsapp/templates', { params: departmentId ? { department_id: departmentId } : {} });

// Chatbot Content
export const getChatbotContent = (contentType) => api.get('/chatbot-content', { params: contentType ? { content_type: contentType } : {} });
export const createChatbotContent = (data) => api.post('/chatbot-content', data);
export const updateChatbotContent = (id, data) => api.put(`/chatbot-content/${id}`, data);
export const deleteChatbotContent = (id) => api.delete(`/chatbot-content/${id}`);

// Broadcast
export const sendBroadcast = (data) => api.post('/broadcast/send', data);
export const previewBroadcast = (params) => api.get('/broadcast/preview', { params });
export const getBroadcastLogs = () => api.get('/broadcast/logs');
export const getBroadcastDetail = (id) => api.get(`/broadcast/${id}`);

// Rep Reports
export const getRepReport = (name, from, to) => api.get(`/analytics/rep-report/${encodeURIComponent(name)}`, { params: { ...(from ? {date_from: from} : {}), ...(to ? {date_to: to} : {}) } });
export const sendRepReportWA = (name) => api.post(`/analytics/send-rep-report/${encodeURIComponent(name)}`);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Analytics
export const getAnalyticsFunnel = (deptId, from, to) => api.get('/analytics/funnel', { params: { ...(deptId ? {department_id: deptId} : {}), ...(from ? {date_from: from} : {}), ...(to ? {date_to: to} : {}) } });
export const getAnalyticsModels = (deptId, from, to) => api.get('/analytics/models', { params: { ...(deptId ? {department_id: deptId} : {}), ...(from ? {date_from: from} : {}), ...(to ? {date_to: to} : {}) } });
export const getAnalyticsReps = (deptId, from, to) => api.get('/analytics/reps', { params: { ...(deptId ? {department_id: deptId} : {}), ...(from ? {date_from: from} : {}), ...(to ? {date_to: to} : {}) } });
export const getAnalyticsChatbot = (deptId, from, to) => api.get('/analytics/chatbot', { params: { ...(deptId ? {department_id: deptId} : {}), ...(from ? {date_from: from} : {}), ...(to ? {date_to: to} : {}) } });
export const getAnalyticsExport = (deptId, from, to) => `${BACKEND_URL}/api/analytics/export?${new URLSearchParams({...(deptId ? {department_id: deptId} : {}), ...(from ? {date_from: from} : {}), ...(to ? {date_to: to} : {})}).toString()}`;
export const getNotifications = () => api.get('/notifications');
export const getMiniAnalytics = () => api.get('/dashboard/mini-analytics');

// Chatbot
export const sendChatbotMessage = (data) => api.post('/chatbot/message', data);

// Settings
export const getSettings = () => cachedGet('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getBotSettings = () => api.get('/bot-settings');
export const getAiModels = () => api.get('/ai-models');

// Internal AI assistant (Yapay Zeka)
export const getAssistantHistory = (session_id) => api.get('/assistant/history', { params: { session_id } });
export const clearAssistantHistory = (session_id) => api.delete('/assistant/history', { params: { session_id } });
export async function askAssistantStream(session_id, question, onDelta, signal) {
  const res = await fetch(`${API}/assistant/ask`, {
    method: 'POST', signal,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
    body: JSON.stringify({ session_id, question }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '', final = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const frames = buf.split('\n\n');
    buf = frames.pop();
    for (const f of frames) {
      if (!f.startsWith('data: ')) continue;
      const ev = JSON.parse(f.slice(6));
      if (ev.delta) onDelta(ev.delta);
      if (ev.done) final = ev;
    }
  }
  return final;
}
export const updateBotSettings = (data) => api.put('/bot-settings', data);
export const getPipelineSettings = () => api.get('/sales-pipeline-settings');
export const updatePipelineSettings = (data) => api.put('/sales-pipeline-settings', data);
export const getPipelineStats = () => api.get('/pipeline-stats');
export const getComplaints = (params) => api.get('/complaints', { params });
export const updateComplaintStatus = (id, data) => api.put(`/complaints/${id}/status`, data);
export const getPipelineLogs = (params) => api.get('/pipeline-logs', { params });
export const getUnansweredLeads = (params) => api.get('/pipeline-unanswered', { params });
export const sendPipelineReminder = (data) => api.post('/pipeline-remind', data);
export const getAppearance = () => axios.get(`${API}/public/appearance`);
export const updateAppearance = (data) => api.put('/appearance', data);
export const getBookingLinks = () => api.get('/booking-links');
export const createBookingLink = (data) => api.post('/booking-links', data);
export const updateBookingLink = (id, data) => api.put(`/booking-links/${id}`, data);
export const deleteBookingLink = (id) => api.delete(`/booking-links/${id}`);

// Mobile app: my data, notifications, web push
export const getMySummary = () => api.get('/my/summary');
export const getMyNotifications = (params) => api.get('/my/notifications', { params });
export const markNotificationsRead = (ids = []) => api.put('/my/notifications/read', { notif_ids: ids });
export const getVapidPublicKey = () => api.get('/push/vapid-public-key');
export const pushSubscribe = (data) => api.post('/push/subscribe', data);
export const pushUnsubscribe = (data) => api.post('/push/unsubscribe', data);
export const getPushStatus = () => api.get('/push/status');
export const sendTestPush = () => api.post('/push/test');
export const pushNativeRegister = (data) => api.post('/push/native-register', data);
export const pushNativeUnregister = (token) => api.post('/push/native-unregister', { token });
export const getFirebaseSettings = () => api.get('/settings/firebase');
export const updateFirebaseSettings = (service_account_json) => api.put('/settings/firebase', { service_account_json });
export const deleteFirebaseSettings = () => api.delete('/settings/firebase');
export const sendDailySummaryNow = () => api.post('/pipeline/daily-summary/send-now');

// Targets & performance
export const getMyPerformance = (month) => api.get('/my/performance', { params: month ? { month } : {} });
export const getAdminOverview = () => api.get('/my/admin-overview');
export const getMyTeam = (month) => api.get('/my/team', { params: month ? { month } : {} });
export const getTargets = (month, departmentId) => api.get('/targets', { params: { ...(month ? { month } : {}), ...(departmentId ? { department_id: departmentId } : {}) } });
export const getDiscountReport = (month) => api.get('/discounts/report', { params: month ? { month } : {} });
export const getAdAnalytics = (month) => api.get('/analytics/ads', { params: month ? { month } : {} });
export const putTarget = (data) => api.put('/targets', data);
export const getDashboardTargets = () => api.get('/dashboard/targets-summary');

// Bot knowledge (learned from human interventions)
export const getBotKnowledge = () => api.get('/bot-knowledge');
export const createBotKnowledge = (data) => api.post('/bot-knowledge', data);
export const updateBotKnowledge = (id, data) => api.put(`/bot-knowledge/${id}`, data);
export const deleteBotKnowledge = (id) => api.delete(`/bot-knowledge/${id}`);
export const approveBotKnowledge = (id) => api.put(`/bot-knowledge/${id}/approve`);
export const rejectBotKnowledge = (id) => api.put(`/bot-knowledge/${id}/reject`);
export const getBotKnowledgePendingCount = () => api.get('/bot-knowledge/pending-count');
export const learnFromConversation = (conversationId) => api.post(`/bot-knowledge/learn-from-conversation/${conversationId}`);
export const updateConversationFacts = (conversationId, facts) => api.put(`/conversations/${conversationId}/facts`, { facts });
export const markLearningSeen = (conversationId) => api.put(`/conversations/${conversationId}/learning-seen`);

export const isAdminRole = (user) => ["admin", "manager"].includes(user?.role);

export default api;

// CRM customer cards
export const getCrmMeta = () => cachedGet('/crm/meta');
export const getCrmStats = () => api.get('/crm/stats');
export const getCrmCustomers = (params) => api.get('/crm/customers', { params });
export const getCrmCustomer = (id) => api.get(`/crm/customers/${id}`);
export const getCrmCustomerByPhone = (phone) => api.get(`/crm/customers/by-phone/${encodeURIComponent(phone)}`);
export const createCrmCustomer = (data) => api.post('/crm/customers', data);
export const updateCrmCustomer = (id, data) => api.put(`/crm/customers/${id}`, data);
export const archiveCrmCustomer = (id) => api.delete(`/crm/customers/${id}`);
export const syncCrmCustomers = () => api.post('/crm/sync');
export const addCrmVehicle = (id, data) => api.post(`/crm/customers/${id}/vehicles`, data);
export const updateCrmVehicle = (id, vid, data) => api.put(`/crm/customers/${id}/vehicles/${vid}`, data);
export const deleteCrmVehicle = (id, vid) => api.delete(`/crm/customers/${id}/vehicles/${vid}`);
export const addCrmContact = (id, data) => api.post(`/crm/customers/${id}/contacts`, data);
export const deleteCrmContact = (cid) => api.delete(`/crm/contacts/${cid}`);
export const addCrmFollowup = (id, data) => api.post(`/crm/customers/${id}/followups`, data);
export const getCrmFollowups = (params) => api.get('/crm/followups', { params });
export const updateCrmFollowup = (fid, data) => api.put(`/crm/followups/${fid}`, data);
export const deleteCrmFollowup = (fid) => api.delete(`/crm/followups/${fid}`);

// CRM Faz 2: import, complaints, alerts, appointment reminders
export const downloadCrmImportTemplate = () => api.get('/crm/import/template', { responseType: 'blob' });
export const previewCrmImport = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/crm/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const commitCrmImport = (id, mode) => api.post(`/crm/import/${id}/commit`, { mode });
export const getCrmImportHistory = () => api.get('/crm/import/history');
export const getComplaintMeta = () => cachedGet('/crm/complaint-meta');
export const createCrmComplaint = (customerId, data) => api.post(`/crm/customers/${customerId}/complaints`, data);
export const updateCrmComplaint = (cid, data) => api.put(`/crm/complaints/${cid}`, data);
export const uploadComplaintDoc = (cid, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/crm/complaints/${cid}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const downloadComplaintDoc = (cid, did) => api.get(`/crm/complaints/${cid}/documents/${did}`, { responseType: 'blob' });
export const deleteComplaintDoc = (cid, did) => api.delete(`/crm/complaints/${cid}/documents/${did}`);
export const sendFollowupAlertsNow = () => api.post('/crm/followups/send-alerts');
export const getApptReminderSettings = () => api.get('/appointment-reminders/settings');
export const updateApptReminderSettings = (data) => api.put('/appointment-reminders/settings', data);
export const sendApptRemindersNow = () => api.post('/appointment-reminders/send-now');

// Messaging (WhatsApp + Mobildev SMS)
export const getMessagingSettings = () => api.get('/messaging/settings');
export const getSmsBalance = () => api.get('/messaging/balance');
export const updateSmsBalance = (data) => api.put('/messaging/balance', data);
export const checkSmsBalance = () => api.post('/messaging/balance/check');
export const getSmsMonthlyReport = (month) => api.get('/messaging/balance/monthly-report', { params: month ? { month } : {} });
export const sendSmsMonthlyReport = () => api.post('/messaging/balance/monthly-report/send');
export const updateMessagingSettings = (data) => api.put('/messaging/settings', data);
export const sendTestSms = (data) => api.post('/messaging/test-sms', data);
export const getSmsLogs = () => api.get('/messaging/sms-logs');

// CRM Faz 3: NPS + gifts / incentives
export const getNps = (params) => api.get('/crm/nps', { params });
export const getNpsSettings = () => api.get('/crm/nps/settings');
export const updateNpsSettings = (data) => api.put('/crm/nps/settings', data);
export const sendCustomerNps = (customerId) => api.post(`/crm/customers/${customerId}/nps`);
export const getPublicNps = (token) => api.get(`/public/nps/${token}`);
export const answerPublicNps = (token, data) => api.post(`/public/nps/${token}`, data);
export const getGiftMeta = () => cachedGet('/crm/gift-meta');
export const getGifts = (params) => api.get('/crm/gifts', { params });
export const createGift = (data) => api.post('/crm/gifts', data);
export const updateGift = (id, data) => api.put(`/crm/gifts/${id}`, data);
export const deleteGift = (id) => api.delete(`/crm/gifts/${id}`);
export const getCustomerFields = () => api.get('/crm/customer-fields');
export const updateCustomerFields = (fields) => api.put('/crm/customer-fields', { fields });
export const getBirthdaySettings = () => api.get('/crm/birthday/settings');
export const updateBirthdaySettings = (data) => api.put('/crm/birthday/settings', data);
export const sendBirthdaysNow = () => api.post('/crm/birthday/send-now');
export const getUpcomingBirthdays = (days = 14) => api.get('/crm/birthday/upcoming', { params: { days } });
export const getCoupons = (params) => api.get('/crm/coupons', { params });
export const redeemCoupon = (code, data = {}) => api.post(`/crm/coupons/${encodeURIComponent(code)}/redeem`, data);
export const getCouponReport = (months = 12) => api.get('/crm/coupons/report', { params: { months } });
export const getMissingSummary = () => api.get('/crm/missing-info/summary');
export const getStaleSettings = () => api.get('/crm/stale/settings');
export const updateStaleSettings = (data) => api.put('/crm/stale/settings', data);
export const sendStaleNow = () => api.post('/crm/stale/send-now');
export const previewCustomerCampaign = (filters) => api.post('/crm/campaigns/preview', filters);
export const createCustomerCampaign = (data) => api.post('/crm/campaigns', data);
export const getCustomerCampaigns = () => api.get('/crm/campaigns');
export const getCampaignResults = (id) => api.get(`/crm/campaigns/${id}/results`);
export const getScoreboard = (month) => api.get('/crm/scoreboard', { params: { month } });
export const getScoreboardSettings = () => api.get('/crm/scoreboard/settings');
export const updateScoreboardSettings = (data) => api.put('/crm/scoreboard/settings', data);
export const downloadExport = async (path, params, name) => {
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v && v !== 'all'));
  const r = await api.get(`/crm/export/${path}`, { params: clean, responseType: 'blob' });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url; a.download = `${name}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};
export const saveBlob = (res, filename) => {
  const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers?.['content-type'] }));
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
